import { useEffect, useRef } from 'react';

/**
 * Plays a soft chime when `signal` value increases.
 * Uses the Web Audio API — no asset needed.
 */
export const useChime = () => {
  const ctxRef = useRef(null);
  const lastRef = useRef(null);

  const ensureCtx = () => {
    if (!ctxRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        ctxRef.current = new Ctx();
      } catch (e) { /* unsupported */ }
    }
    return ctxRef.current;
  };

  const play = () => {
    const ctx = ensureCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    [ [880, 0], [1175, 0.12], [1568, 0.24] ].forEach(([freq, t]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + t);
      gain.gain.linearRampToValueAtTime(0.18, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.32);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.34);
    });
  };

  const onSignal = (signalValue) => {
    if (lastRef.current !== null && signalValue > lastRef.current) play();
    lastRef.current = signalValue;
  };

  return { play, onSignal };
};

export default function SoundNotifier({ signalValue }) {
  const { onSignal } = useChime();
  useEffect(() => { onSignal(signalValue); /* eslint-disable-next-line */ }, [signalValue]);
  return null;
}
